/** Article-specific product-research prompts. Brand examples are research
 * subjects, not a verified app-coverage list or particle/safety verdict.
 * Conversion and activation benefit are hypotheses to measure after release. */
export interface FirstScanPrompt {
  product: string
  brands: string
  payoff: string
  ctaLabel?: string
  webHref: string
  webLabel: string
}

export const FIRST_SCAN_CALLOUT_METHOD = 'visible_callout_heading_2026_10_06'
export const FIRST_SCAN_PROMPTS: Record<string, FirstScanPrompt> = {
  "microplastics-in-pregnancy": {"product": "one food container or pregnancy product you use regularly", "brands": "the exact item, all contact components and stated claim or test conditions", "payoff": "Keep material, chemical and particle questions separate. A product assessment does not measure internal burden or predict a pregnancy outcome.", "webHref": "/blog/microplastics-in-prenatal-vitamins", "webLabel": "Read the prenatal label guide"},
  "microplastics-in-prenatal-vitamins": {"product": "your exact prenatal label and package", "brands": "the full Supplement Facts panel, serving, ingredients and storage directions", "payoff": "Separate formulation and packaging questions from a particle test. Product information cannot select a prenatal dose or authorize changing supplementation.", "webHref": "/blog/microplastics-in-pregnancy", "webLabel": "Read the pregnancy evidence and its limits"},
  "microplastics-nursery-guide": {"product": "one nursery item or household product", "brands": "the exact model, declared components, compatible use and care instructions", "payoff": "Keep safe sleep and hygiene requirements intact. A product assessment does not approve a sleep setup or measure your room’s air.", "webHref": "/blog/microplastics-in-baby-products", "webLabel": "Use the baby-product component checklist"},

  "microplastics-in-baby-food-pouches": {
  "product": "the exact purée package and serving container",
  "brands": "your product name, package size, closure and current handling label",
  "payoff": "Review the exact package and permitted use. An assessment does not measure the purée, certify a particle-free serving or change storage instructions.",
  "webHref": "/blog/microplastics-in-baby-products",
  "webLabel": "Use the baby-product component checklist"
},
  "microplastics-and-children": {
  "product": "one food or drink container your family uses regularly",
  "brands": "the exact product model, contact components and care instructions",
  "payoff": "Keep material declarations separate from release tests and child health. A product assessment cannot measure a child’s dose or tissue burden.",
  "webHref": "/blog/microplastics-in-baby-products",
  "webLabel": "Check a product without an exposure ranking"
},
  "microplastics-in-baby-products": {
  "product": "one bottle, serving container or baby item",
  "brands": "the exact model, declared materials, approved accessories and care label",
  "payoff": "Review documented product information. Preserve feeding, safe sleep and car-seat requirements; an assessment cannot certify a meal or approve an accessory.",
  "webHref": "/blog/microplastics-and-children",
  "webLabel": "Read the children’s research and its limits"
},

  "microplastics-in-bottled-water": {
    "product": "the water source, bottle and filter model you use",
    "brands": "your exact water package, closure and replacement filter element",
    "payoff": "Keep source-water guidance, packaging disclosures and model-specific reduction claims separate. A product assessment cannot measure the water or establish drinking-water safety.",
    "webHref": "/data/bottled-water-brand-database",
    "webLabel": "Read the source-linked water evidence table"
},
  "does-fiji-water-have-microplastics": {
    "product": "the exact FIJI bottle size and market you buy",
    "brands": "330 mL, 500 mL, 700 mL, sports cap, 1.0 L and 1.5 L formats",
    "payoff": "Record the format and bottle-only material disclosure. Keep reported brand testing separate from an unnamed study average or a test of your own bottle.",
    "webHref": "/data/bottled-water-brand-database",
    "webLabel": "Check the historical bottled-water evidence"
},

  "microplastics-in-oat-milk-brands-ranked": {
    "product": "the exact oat-milk variety and package you buy",
    "brands": "Oatly, Chobani, Planet Oat, Califia, Silk, Trader Joe’s and Minor Figures",
    "payoff": "Record the product, market, package and label instructions. Material and ingredient disclosures cannot measure the milk or establish the least-contaminated brand.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-travel-toiletries-hotel-water-plane-food": {
    "product": "the exact toiletries, bottle and coffee cup you plan to pack",
    "brands": "your product labels, container capacities, bottle closures and cup instructions",
    "payoff": "Keep the exact product and component questions together. Product information does not verify drinking-water safety, measure a formula or replace airport and customs guidance.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review a travel toiletry ingredient list"
  },
  "does-essentia-water-have-microplastics": {
    "product": "your Essentia bottle and the water format you use most days",
    "brands": "Essentia, home RO, Brita, ZeroWater, glass and stainless bottles",
    "payoff": "Identify the current bottle format and any filter model you compare it with. Keep packaging disclosures separate from a measurement of the water inside.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-vaseline-have-microplastics": {
    "product": "your exact Vaseline jar, lotion, stick or lip product",
    "brands": "Original Healing Jelly, Cocoa Butter, body lotion, balm stick, lip therapy",
    "payoff": "Keep the exact product, market and full ingredient panel together. A finding for one jar cannot be transferred to every lotion, stick or lip formula.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review an ingredient list on the web"
  },
  "does-colgate-toothpaste-have-microplastics": {
    "product": "the ingredient panel on your toothpaste tube",
    "brands": "Colgate Total, Optic White, Max Fresh, Sensitive, kids toothpaste",
    "payoff": "Use the full ingredient panel for your exact tube and market. Review declared names without turning a name match into a particle measurement or brand-wide verdict.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review an ingredient list on the web"
  },
  "are-nylon-cooking-utensils-plastic": {
    "product": "your most-used spatula or cooking spoon",
    "brands": "nylon or PA, polypropylene, silicone, wood, stainless steel",
    "payoff": "Find the molded material mark, model and permitted use. Colour alone cannot identify the polymer or establish release during cooking.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "is-polyester-clothing-plastic": {
    "product": "the care and fibre label on your most-washed garment",
    "brands": "polyester activewear, fleece, school uniforms, cotton-poly blends",
    "payoff": "Record the fibre blend and garment care label. Composition and washing frequency are useful context, but they do not quantify personal exposure.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-parchment-paper-have-plastic": {
    "product": "your roll or box of cooking paper",
    "brands": "Reynolds parchment, store-brand parchment, freezer paper, wax paper",
    "payoff": "Find the exact paper type, disclosed coating and temperature instructions on the box. Check that the intended use matches the manufacturer’s directions.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-evian-have-microplastics": {
    "product": "the Evian package you actually buy",
    "brands": "Evian PET, Evian glass, filtered tap, glass and stainless bottles",
    "payoff": "Identify the current package and lot where available. Historical sampled bottles do not supply a particle count for the bottle in your hand.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "chewing-gum-brands-ranked-microplastics": {
    "product": "the gum pack in your bag or car",
    "brands": "Trident, Extra, Orbit, 5 Gum, Simply Gum, Glee, PUR",
    "payoff": "Check the exact pack’s ingredient and gum-base disclosures. An undisclosed base remains a question; a material preference does not establish a particle-free gum.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-graza-have-microplastics": {
    "product": "your olive-oil bottle",
    "brands": "Graza squeeze bottle, glass-bottled EVOO, metal tin",
    "payoff": "Identify the oil format and package separately. A bottle material cannot establish which stage contributed particles to a tested oil sample.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "are-nalgene-bottles-safe-microplastics": {
    "product": "your exact Nalgene bottle and lid",
    "brands": "Nalgene Tritan Renew, older polycarbonate Nalgene, stainless and glass bottles",
    "payoff": "Find the bottle model, material marking and lid details. Keep an older bottle’s documentation separate from a current model’s disclosures.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-sparkling-water": {
    "product": "the sparkling-water format in your fridge",
    "brands": "LaCroix, Bubly, Spindrift, Waterloo, Topo Chico, Perrier",
    "payoff": "Identify the can, plastic bottle or glass format, including its closure or lining where disclosed. The format does not provide a measured count for the drink.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "do-microplastic-removal-supplements-work": {
    "product": "the supplement label or routine product you want to understand",
    "brands": "supplement label, evidence claim, bottled water, food container",
    "payoff": "Record the supplement’s exact label and evidence claim. An app assessment cannot validate removal of particles from the body or substitute for appropriate clinical advice.",
    "webHref": "/methodology",
    "webLabel": "Read the evidence and model limitations"
  },
  "foods-that-help-clear-microplastics": {
    "product": "your groceries and their packaging",
    "brands": "bottled water, canned food, plastic-wrapped produce, gum, seafood",
    "payoff": "Choose one food or package question to investigate. An ingredient, packaging format or app score cannot demonstrate that a food clears particles from the body.",
    "webHref": "/methodology",
    "webLabel": "Read the evidence and model limitations"
  },
  "do-energy-drinks-have-microplastics": {
    "product": "your energy drink",
    "brands": "Celsius, Red Bull, Monster, Bang, Reign, Prime Hydration, Prime Energy",
    "payoff": "Keep the exact drink and package format together. Review disclosed materials and ingredients without using the container to calculate particles in the drink.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "best-electrolyte-powders-without-microplastics": {
    "product": "your electrolyte packets and shaker bottle",
    "brands": "Liquid IV, LMNT, Nuun, Ultima, Gatorade, Powerade",
    "payoff": "Review the packet, ingredient panel, mixing-water source and shaker as separate questions. None of those labels supplies a particle count for the prepared drink.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-fairlife-have-microplastics": {
    "product": "your protein shake",
    "brands": "Fairlife Core Power, Premier Protein, OWYN, Muscle Milk",
    "payoff": "Keep the exact product, pack size and container format together. Ingredient, plasticiser and particle findings are different kinds of evidence and need their own sources.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "plastic-wrap-ziploc-bags-microplastics": {
    "product": "your wrap roll or storage-bag box",
    "brands": "Reynolds Quick Cut, Glad Cling ’N Seal, Ziploc storage families",
    "payoff": "Match the exact product and task before applying material, heating or reuse directions. A label or app assessment cannot supply a particle count or authorize cooking.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-sous-vide": {
    "product": "your sous vide or vacuum-seal bags",
    "brands": "FoodSaver, Anova, Ziploc, reusable silicone bags",
    "payoff": "Find the exact bag’s permitted temperature, duration and appliance use. A generic food-contact claim does not establish every cooking use or quantify release.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-ice": {
    "product": "your ice trays and water filter",
    "brands": "silicone trays, stainless trays, plastic trays, Brita, PUR, fridge ice makers",
    "payoff": "Record the tray model, material and water source separately. Their identities do not establish a particle count or the benefit of replacing one component.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-and-heart-attacks": {
    "product": "one everyday product question you want to research",
    "brands": "bottled water, plastic food containers, filter pitchers",
    "payoff": "Choose a product-information question you want to understand. A scan cannot translate an observational health study into your personal cardiovascular risk or a measured benefit from a swap.",
    "webHref": "/methodology",
    "webLabel": "Read the evidence and model limitations"
  },
  "microplastics-in-k-cups-and-coffee-pods": {
    "product": "your K-Cup or pod brand",
    "brands": "Keurig, Nespresso, Green Mountain, Starbucks, Peet's, Folgers",
    "payoff": "Record the exact capsule family and its components. Compare disclosed construction and source scope, rather than assigning tea-bag counts or a particle ranking to coffee pods.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "nespresso-vertuo-vs-original-microplastics": {
    "product": "your Nespresso pod (Vertuo or Original)",
    "brands": "Nespresso Vertuo, Nespresso Original, Starbucks Nespresso, L'Or, third-party pods",
    "payoff": "Identify the exact capsule line and version. Separate body, coating, lid and filter information before comparing Original and Vertuo products.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-coffee-by-brewing-method": {
    "product": "your brewer, filter and drinking cup",
    "brands": "K-Cup, French press, drip, pour-over, Aeropress, instant",
    "payoff": "Trace the brewer, filter, water vessel and drinking cup. This organizes the contact path; it does not turn a method comparison into a measurement of your coffee.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-espresso-machines": {
    "product": "your exact espresso-machine model and documentation",
    "brands": "Breville, Gaggia, Rancilio, Rocket, La Marzocco, Flair, Philips, De’Longhi, JURA",
    "payoff": "Match the machine generation and market to its reservoir, brewing-path and care documents. A barcode or photograph cannot identify every concealed tube, seal or coating or measure particles in coffee.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-coffee-cups": {
    "product": "your takeaway cup or reusable mug and its separate lid",
    "brands": "paper cup, cold cup, travel mug, cup barrier, lid, valve, seal",
    "payoff": "Record the exact format and available material declarations. A saved product record cannot measure your drink or convert a generic cup experiment into your serving dose.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-starbucks-coffee-contain-microplastics": {
    "product": "a Starbucks cup, sleeve, or bottled drink",
    "brands": "Starbucks hot cup, cold cup, Reserve, bottled Frappuccino, K-Cup",
    "payoff": "Keep the exact hot, iced, bottled or capsule format together. Check cup and lid disclosures where available without converting them into particles per serving.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-dunkin-coffee-contain-microplastics": {
    "product": "your Dunkin' cup or pod",
    "brands": "Dunkin' hot cup, iced cup, Original Blend K-Cup, bottled iced coffee",
    "payoff": "Identify the cup, lid, bottle or capsule you actually use. Missing supplier material information remains uncertain; the brand name does not measure the drink.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-iced-coffee-brands-ranked-summer-2026": {
    "product": "whichever iced coffee you bought today",
    "brands": "Starbucks, Dunkin', McDonald's, Peet's, Tim Hortons, Caribou, STōK, Califia",
    "payoff": "Record the exact drink format and available packaging details. A saved product record cannot establish which brand changed your personal exposure.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-instant-coffee-have-microplastics": {
    "product": "your instant coffee jar or canister, and the mug",
    "brands": "Nescafé, Taster's Choice, Starbucks VIA, Folgers, Café Bustelo, Mount Hagen",
    "payoff": "Identify the coffee package, kettle and mug separately. Review their disclosed components without ranking them as measured sources of your personal intake.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "best-plastic-free-coffee-makers": {
    "product": "your current coffee maker and its filter pack",
    "brands": "Caraway, Simply Good, Chemex, Frieling, Moccamaster, AeroPress",
    "payoff": "Record the exact model and compare its manufacturer disclosures for the reservoir, basket, filter and seals. A scan does not identify every concealed component or measure the particles in your coffee.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-aeropress-leach-microplastics": {
    "product": "your exact AeroPress brewer, cap, filter and receiving vessel",
    "brands": "Original, Original XL, Go, Clear, Clear XL, Go Plus, Premium, Steel",
    "payoff": "Match the model and accessories to their material and care documents. Keep silicone seals and conflicting cap declarations visible; a scan cannot measure particles or resolve an undocumented component.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "is-moccamaster-plastic-free": {
    "product": "your Moccamaster model, basket and actual carafe",
    "brands": "KBGV Select, KBT, item 53054 outlet arm, item 13192 manual basket",
    "payoff": "Check the exact compatible parts and carafe generation. A scan does not reveal every internal seal or establish a particle dose from the copper heater or glass vessel.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-cirkul-have-microplastics": {
    "product": "your Cirkul bottle and a Sip cartridge box",
    "brands": "Cirkul, Owala, Stanley, Hydro Flask, Gatorade Gx",
    "payoff": "Keep the bottle, lid and cartridge as separate components. A steel bottle body does not establish the cartridge’s materials or a measured benefit from changing the system.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "are-vacuum-sealer-bags-safe-microplastics": {
    "product": "your vacuum sealer bag roll or box",
    "brands": "FoodSaver, Nesco, Anova, Wevac, Ziploc vacuum",
    "payoff": "Check the exact roll or bag’s material disclosures and approved cooking/storage uses. Follow its directions; an app assessment does not authorize a different temperature or duration.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "do-air-fryers-release-microplastics": {
    "product": "your air fryer basket and its liner pack",
    "brands": "Ninja, COSORI, Philips, Instant Vortex, Chefman, plus any liner brand",
    "payoff": "Identify the appliance model, basket surface and exact liner. Check compatibility and care directions instead of treating a coating name as a measured particle result.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "are-breast-pumps-safe-microplastics": {
    "product": "your pump parts and collection bottles",
    "brands": "Exact Medela, Spectra or original Elvie Pump kit and part number",
    "payoff": "Record the pump model, parts and collection-bottle documentation. Keep prescribed cleaning and compatible-part instructions in place; an assessment cannot validate a parts substitution.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "red-solo-cups-plastic-party-supplies-microplastics": {
    "product": "the party supplies you already bought",
    "brands": "Solo, Dixie, Chinet, Hefty, Great Value, Member's Mark",
    "payoff": "Check each cup, plate and cutlery pack’s material and intended use. Different constructions are useful product information, not a personal health ranking.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "water-bottle-left-in-hot-car-microplastics": {
    "product": "the exact bottle and storage instructions for your trip",
    "brands": "Sealed single-use bottle, opened bottle, case, reusable bottle with separate lid and seals",
    "payoff": "Record disclosed components and care instructions. A photo cannot reconstruct heat history, measure particles or certify the water after car storage.",
    "webHref": "/blog/microplastics-in-bottled-water",
    "webLabel": "Review bottled-water evidence"
  },
  "stanley-vs-yeti-vs-hydroflask-vs-owala-microplastics": {
    "product": "the bottle on your desk right now",
    "brands": "Stanley Quencher, Yeti Rambler, Hydro Flask, Owala FreeSip, Stojo",
    "payoff": "Identify the exact bottle, lid, straw and seals. Check model-specific disclosures rather than applying one configuration’s information to an entire brand.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "best-glass-water-bottles": {
    "product": "the exact glass bottle you are considering",
    "brands": "Lifefactory 22oz Classic Cap, bkr, Purifyou, Ello Clara",
    "payoff": "Review the vessel, closure, seal and sleeve separately. A glass body is a construction detail, not a whole-product absence finding.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "best-stainless-steel-water-bottles": {
    "product": "the exact steel bottle you are considering",
    "brands": "Klean Kanteen, Hydro Flask, Yeti, Thermos, Stanley",
    "payoff": "Find the exact vessel, any lining or coating, and the lid assembly. A steel body or photograph cannot quantify release from the complete product.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-bottled-water-brands-ranked": {
    "product": "whatever bottled water is in your fridge",
    "brands": "Aquafina, Dasani, Fiji, Smartwater, Evian, Poland Spring",
    "payoff": "Keep the current bottle’s identity separate from published sample results. Unnamed study brands and historical lots cannot supply a current brand or bottle particle ranking.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-drinking-water-by-country": {
    "product": "the bottled or filtered water you're drinking right now",
    "brands": "Tap, Brita, ZeroWater, LifeStraw, Berkey, Evian, Aquafina",
    "payoff": "Record the actual water source, filter model and available test scope. A country comparison is not a measurement of water from your tap.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-water-filters-compared": {
    "product": "the filter you currently use",
    "brands": "Brita Standard, Brita Elite, ZeroWater, LifeStraw, Berkey, PUR",
    "payoff": "Find the exact filter and covered system, test endpoint, particle-size range and maintenance instructions. A brand or stage count does not establish universal removal.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "do-brita-filters-remove-microplastics": {
    "product": "your Brita (or whatever pitcher you own) + the bottle you pour into",
    "brands": "Brita Standard, Brita Elite, ZeroWater, Clearly Filtered, PUR",
    "payoff": "Check the cartridge model and its covered-system performance sheet, then the vessel’s care instructions. A filtration claim and a claim about particles released by the bottle require separate evidence.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "do-ziploc-bags-release-microplastics": {
    "product": "the exact bag you use for storage, freezing or reheating",
    "brands": "Ziploc Sandwich, Storage, Freezer, Zip ’n Steam, Endurables",
    "payoff": "Review disclosed film and closure materials separately from intended use. A materials record or modeled contact assessment is not a measured particle count, exposure or health-risk ranking.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-canned-food": {
    "product": "a can from your pantry",
    "brands": "Campbell's, Progresso, Bumble Bee, Eden Organic, Amy's",
    "payoff": "Review the exact can and any disclosed lining. A BPA-free claim addresses a particular substance; it does not establish a particle-free food or an unrestricted coating verdict.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-plastic-containers": {
    "product": "your most-used food container",
    "brands": "Rubbermaid, Tupperware, Pyrex with plastic lid, Glasslock, Stasher",
    "payoff": "Identify the vessel, lid and seals, then their separate use instructions. A model score cannot establish which component releases the most particles.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "what-does-microwave-safe-actually-mean": {
    "product": "the exact container and lid you use for reheating",
    "brands": "Product family, model, material declaration and care instructions",
    "payoff": "Keep product-specific heating permissions separate from a particle measurement. A photo or model score cannot validate microwave use.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microwaving-plastic-containers-microplastics": {
    "product": "your usual reheating vessel and cover",
    "brands": "Exact container, lid and manufacturer instructions",
    "payoff": "Review the main contact surface and directions. An assessment cannot measure particles in your meal or infer a personal health outcome.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "best-plastic-free-food-storage": {
    "product": "a container you're considering swapping to",
    "brands": "Pyrex Freshlock, IKEA 365+, ECOlunchbox, Stasher",
    "payoff": "Check the whole assembly before comparing alternatives, including plastic lids and seals on glass containers. A material preference does not certify the replacement as particle-free.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-from-breast-pump-parts": {
    "product": "your pump bottle or flange",
    "brands": "Exact Medela, Spectra or original Elvie Pump kit and part number",
    "payoff": "Keep the exact part number, compatibility and cleaning directions together. An app assessment cannot establish a safer pumping routine or validate a substitute part.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "best-baby-bottles-microplastic-free": {
    "product": "the baby bottle you're feeding from today",
    "brands": "Dr. Brown's glass, Philips Avent glass, Comotomo, Lansinoh, Pigeon",
    "payoff": "Check the complete bottle, teat, vent, collar and seals, with their preparation and care instructions. Material disclosures do not establish a particle-free assembly or infant health benefit.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-breast-milk": {
    "product": "whatever storage bag or bottle you pump into",
    "brands": "Lansinoh storage bags, Medela bottles, Kiinde, Dr. Brown's",
    "payoff": "Identify the exact storage bag or bottle and its permitted use. A storage material or app score cannot establish the source of particles in a human sample.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-disposable-diapers": {
    "product": "the diaper you're currently using",
    "brands": "Pampers, Huggies, Honest, Hello Bello, Coterie, Dyper",
    "payoff": "Review the exact diaper’s layer and material disclosures where available. A natural-material claim or photograph cannot provide a measured particle or skin-health result.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-fruits-and-vegetables": {
    "product": "the produce sitting in your fridge",
    "brands": "Pre-cut bagged salad, berries in clamshells, frozen veg pouches, plastic-wrapped cucumber",
    "payoff": "Separate the produce, growing/processing context and package. Packaging identity alone cannot establish the main source or amount of particles in the food.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-tea-bags": {
    "product": "the tea bag you steeped this morning",
    "brands": "tea-bag material disclosure, infuser, loose-leaf tea",
    "payoff": "Find the exact bag or infuser’s material disclosure. Keep the tested product and analytical method attached to any published result; a pyramid shape is not a universal release ranking.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "microplastics-in-milk-and-dairy": {
    "product": "the milk carton or yogurt cup in your fridge",
    "brands": "Horizon, Organic Valley, Fairlife, Chobani, Oikos, Siggi's",
    "payoff": "Keep the exact dairy product and package format together. A carton appearance cannot identify every lining or quantify particles in its contents.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "how-to-test-for-microplastics-at-home": {
    "product": "the bottle, container, or appliance you most want to test",
    "brands": "Any product you use daily",
    "payoff": "Write down the sample and question you want a test to answer. An app organizes product information; it does not replace validated sampling and laboratory identification.",
    "webHref": "/methodology",
    "webLabel": "Read the evidence and model limitations"
  },
  "how-long-do-microplastics-stay-in-your-body": {
    "product": "one routine product or evidence question",
    "brands": "Bottled water, K-Cups, takeout containers, tea bags, cosmetics",
    "payoff": "Choose a product or source question to research. An app score cannot determine retention time, particles in your body or a change in personal intake.",
    "webHref": "/methodology",
    "webLabel": "Read the evidence and model limitations"
  },
  "protein-shakes-ranked-microplastics-phthalates": {
    "product": "the shake in your fridge",
    "brands": "Fairlife Core Power, Premier Protein, Muscle Milk, Orgain, OWYN, SlimFast",
    "payoff": "Identify the exact shake and container format, then check available ingredient and material disclosures. Missing information stays uncertain; a camera image cannot supply an undisclosed polymer.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-premier-protein-have-microplastics": {
    "product": "your Premier Protein carton or bottle",
    "brands": "Premier Protein shakes, Protein Sodas, Coffeehouse, Indulgence",
    "payoff": "Record the exact product and carton, bottle or other format. One container’s disclosures cannot be transferred to every size or formulation.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-muscle-milk-have-microplastics": {
    "product": "your Muscle Milk bottle",
    "brands": "Muscle Milk Protein Shake, Muscle Milk Pro, 11oz and 14oz",
    "payoff": "Use the exact current product and label alongside manufacturer information. Unsuccessful website extraction is not proof that the company publishes no ingredient list.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "drugstore-skincare-microplastics-audited": {
    "product": "the moisturiser, cleanser and SPF on your shelf",
    "brands": "CeraVe, Cetaphil, Neutrogena, La Roche-Posay, Aveeno, Olay",
    "payoff": "Keep the exact current label with your product research. The historical archive has incomplete text coverage, and a name match cannot measure particles.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review an ingredient list on the web"
  },
  "does-cerave-have-microplastics": {
    "product": "your CeraVe bottle or tub",
    "brands": "Moisturizing Cream, Daily Moisturizing Lotion, Foaming Cleanser, SPF lotions",
    "payoff": "Record the exact product, market and full ingredient panel. Cream, balm, gel and SPF products require separate records; a photo cannot certify particle absence.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review an ingredient list on the web"
  },
  "does-cetaphil-have-microplastics": {
    "product": "your Cetaphil cleanser or SPF moisturiser",
    "brands": "Gentle Skin Cleanser, Moisturizing Lotion, Daily Facial Moisturizer SPF 15, DermaControl",
    "payoff": "Keep the full current label and product version. A cleanser, SPF lotion and removable patch need separate records rather than a brand verdict.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review an ingredient list on the web"
  },
  "can-you-microwave-ziploc-bags": {
    "product": "the exact Ziploc bag or silicone pouch you plan to heat",
    "brands": "Ziploc Storage, Freezer, Zip ’n Steam, Compostable, Endurables",
    "payoff": "Keep the current product and market with its task and closure instructions. Generic FAQs and dated product sheets may differ; a scan cannot authorize heat or measure particles.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "nanoplastics-placenta-hormones-study-2026": {
    "product": "the product or study claim you want to understand",
    "brands": "Takeaway containers, cling film, PET water bottles, foam cups",
    "payoff": "Keep a specific product question separate from the experimental study. A scan cannot establish placental transfer, pregnancy risk or a measured benefit from changing packaging.",
    "webHref": "/methodology",
    "webLabel": "Read the evidence and model limitations"
  },
  "7-day-microplastic-detox-challenge": {
    "product": "one everyday product you want to understand",
    "brands": "Bottled water, K-Cups, takeout containers, microwave plastic, tea bags",
    "payoff": "Choose one routine product to understand and record its disclosed information. A seven-day activity or app assessment cannot demonstrate detoxification or clearance of particles from the body.",
    "webHref": "/methodology",
    "webLabel": "Read the evidence and model limitations"
  },
  "can-you-microwave-solo-cups": {
    "product": "the cups and lids you actually drink hot things from",
    "brands": "Solo, Dixie, Hefty, Chinet, coffee-shop cups and lids",
    "payoff": "Find the exact cup’s material and permitted use, with separate lid instructions. A brand name or photograph is not approval to microwave a cup.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-neutrogena-have-microplastics": {
    "product": "your Neutrogena moisturiser, cleanser or SPF",
    "brands": "Hydro Boost, Rapid Wrinkle Repair, Ultra Sheer, Deep Clean, Body Clear",
    "payoff": "Preserve the exact current label, market and version. Historical name matches cannot supply a current particle, legal or health verdict.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review an ingredient list on the web"
  },
  "can-you-microwave-rubbermaid": {
    "product": "the container you reheat leftovers in",
    "brands": "Rubbermaid Brilliance, TakeAlongs, Easy Find Lids, Tupperware, Ziploc containers",
    "payoff": "Identify the exact product line and check current vessel and lid instructions. A generic brand answer or an app assessment cannot supply a verified temperature limit.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "do-contact-lenses-shed-microplastics": {
    "product": "your exact lens, case and solution labels",
    "brands": "Acuvue, Dailies, Biofinity, Air Optix, Precision1, Opti-Free, Renu, Biotrue",
    "payoff": "Identify the exact lens, case and solution, keeping prescribed care and compatibility instructions in place. An assessment cannot validate a replacement in an eye-care routine.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "do-gel-and-acrylic-nails-shed-microplastics": {
    "product": "the polish, base coat or top coat in your drawer",
    "brands": "OPI, Essie, Sally Hansen, Olive & June, CND Shellac, Gelish, Static Nails",
    "payoff": "Review the complete ingredient list for the exact polish or coating. Declared polymer names and free-from claims do not establish physical form, concentration or particle release.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review an ingredient list on the web"
  },
  "think-dirty-vs-yuka": {
    "product": "the exact product and label behind another app’s score",
    "brands": "CeraVe, The Ordinary, Neutrogena, Cetaphil, Drunk Elephant, e.l.f.",
    "payoff": "Keep the exact product and ingredient list when comparing app outputs. Different screening rules do not supply a particle measurement or prove that another app misses every polymer.",
    "webHref": "/microplastic-ingredient-checker",
    "webLabel": "Review an ingredient list on the web"
  },
  "microplastic-testing": {
    "product": "the filter jug, bottled water or food packaging you already own",
    "brands": "Brita, ZeroWater, PUR, LifeStraw, Aquasana, Berkey",
    "payoff": "Identify the exact sample, size range and analytical question before comparing testing claims. A product scan and a laboratory particle measurement serve different purposes.",
    "webHref": "/methodology",
    "webLabel": "Read the evidence and model limitations"
  },
  "does-hydrojug-traveler-have-microplastics": {
    "product": "your exact HydroJug Traveler and lid assembly",
    "brands": "HydroJug Traveler body, lid, straw, seals",
    "payoff": "Check the current model’s body, lid, straw and seals separately. A steel vessel does not disclose every contact component or establish a measured benefit.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "are-stasher-bags-safe": {
    "product": "your exact Stasher bag and intended use",
    "brands": "Stasher bag, seal, care instructions",
    "payoff": "Keep the material disclosure and permitted use together. Silicone construction and a care instruction are not a whole-product particle-clearance result.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-contigo-autoseal-have-microplastics": {
    "product": "your exact Contigo AUTOSEAL bottle and lid",
    "brands": "Contigo model, lid mechanism, seal, care guide",
    "payoff": "Review the exact bottle and lid mechanism, including cleaning access and disclosed materials. A brand-level answer cannot identify every model’s assembly.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-camelbak-eddy-have-microplastics": {
    "product": "your exact CamelBak eddy+ bottle and drinking parts",
    "brands": "CamelBak eddy+ body, cap, bite valve, straw",
    "payoff": "Identify the current bottle version, cap, valve and straw. Keep component disclosures separate from an estimate or a measurement of particles in a drink.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "pyrex-freshlock-vs-snapware-microplastics": {
    "product": "the exact Freshlock or Snapware container and lid",
    "brands": "Pyrex Freshlock, Snapware vessel, lid, seals",
    "payoff": "Compare the vessel, lid and seals with their own instructions. The glass body does not establish that the whole assembly is plastic-free or suitable for every heating use.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "ikea-365-glass-containers-microplastics": {
    "product": "your exact IKEA 365+ container and lid",
    "brands": "IKEA 365+ vessel, lid, seals, product number",
    "payoff": "Use the product number to find vessel and lid instructions separately. A glass-container heating instruction cannot be transferred automatically to a plastic lid.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-ninja-crispi-have-microplastics": {
    "product": "your exact Ninja CRISPi vessel and accessories",
    "brands": "Ninja CRISPi vessel, adapter, lid, cooking accessories",
    "payoff": "Identify the exact cooking system and accessory instructions. A glass vessel does not establish the materials or permitted use of every lid and adapter.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "zerowater-vs-brita-microplastics": {
    "product": "your exact cartridge and compatible pitcher",
    "brands": "ZeroWater, Culligan MaxClear, Brita Standard, Brita Elite",
    "payoff": "Check the cartridge, covered system and specific test claim. TDS readings and another product line’s documentation do not establish your filter’s microplastics performance.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-hario-v60-have-microplastics": {
    "product": "your exact Hario V60 dripper and filter",
    "brands": "Hario V60 dripper material, filter, server",
    "payoff": "Keep the dripper, filter and server identities separate. A material version or photograph is not a measurement of particles in the brewed coffee.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-chemex-have-microplastics": {
    "product": "your exact Chemex brewer and filter",
    "brands": "Chemex vessel, filter, accessories",
    "payoff": "Identify the vessel and filter you actually use. Review their construction and care information without treating the brewing method as a whole-cup particle assay.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "reusable-nespresso-capsules-microplastics": {
    "product": "your exact reusable capsule and compatible brewer",
    "brands": "Reusable capsule body, lid, seals, brewer compatibility",
    "payoff": "Find the capsule’s exact body, lid, seal and compatibility documentation. Reuse or a metal body does not establish zero release or approve it for another brewer.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  },
  "does-ember-mug-have-microplastics": {
    "product": "your exact Ember Mug and food-contact surface",
    "brands": "Ember Mug model, surface, lid, care instructions",
    "payoff": "Check the model-specific surface and care documentation, including any lid. Temperature control and the outer body do not establish a particle count for the drink.",
    "webHref": "/risk-checker",
    "webLabel": "Build a product-routine reading plan"
  }
}

export function getFirstScanPrompt(slug: string): FirstScanPrompt | undefined {
  return FIRST_SCAN_PROMPTS[slug]
}
